import React, { useState } from "react";
import Link from "next/link";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { theme } from "@/styles/theme";
import { media } from "@/styles/media";
import { IFontProps } from "@/typings/Props";

export const CONTACT_TYPES = [
  {
    label: "contact-us.contact-type-1",
    value: "sales",
  },
  {
    label: "contact-us.contact-type-2",
    value: "support",
  },
  {
    label: "contact-us.contact-type-3",
    value: "etc",
  },
];

export default function ContactUs() {
  const { t } = useTranslation();
  const [contactType, setContactType] = useState<string>(CONTACT_TYPES[0].value);
  const [form, setForm] = useState<{
    [k: string]: {
      label: string;
      type: React.HTMLInputTypeAttribute;
      conditions: {
        func: (v: string | number) => boolean;
        msg: string;
      }[];
      beginInput: boolean;
      value: string | number;
    };
  }>({
    lastName: {
      label: t("contact-us.form-name-last"),
      type: "text",
      conditions: [
        {
          func: (value: string | number) => String(value).length > 0,
          msg: "This field is required",
        },
      ],
      beginInput: false,
      value: "",
    },
    firstName: {
      label: t("contact-us.form-name-first"),
      type: "text",
      conditions: [
        {
          func: (value: string | number) => String(value).length > 0,
          msg: "This field is required",
        },
      ],
      beginInput: false,
      value: "",
    },
    country: {
      label: t("contact-us.form-country"),
      type: "text",
      conditions: [
        {
          func: (value: string | number) => String(value).length > 0,
          msg: "This field is required",
        },
      ],
      beginInput: false,
      value: "",
    },
    city: {
      label: t("contact-us.form-city"),
      type: "text",
      conditions: [
        {
          func: (value: string | number) => String(value).length > 0,
          msg: "This field is required",
        },
      ],
      beginInput: false,
      value: "",
    },
    companyName: {
      label: t("contact-us.form-company-name"),
      type: "text",
      conditions: [
        {
          func: (value: string | number) => String(value).length > 0,
          msg: "This field is required",
        },
      ],
      beginInput: false,
      value: "",
    },
    email: {
      label: t("contact-us.form-email"),
      type: "email",
      conditions: [
        {
          func: (value: string | number) => String(value).length > 0,
          msg: "This field is required",
        },
      ],
      beginInput: false,
      value: "",
    },
    phoneNumber: {
      label: t("contact-us.form-phone-number"),
      type: "tel",
      conditions: [
        {
          func: (value: string | number) => String(value).length > 0,
          msg: "This field is required",
        },
      ],
      beginInput: false,
      value: "",
    },
  });
  const [detailEnquiry, setDetailEnquiry] = useState<string>("");
  const [successInquiry, setSuccessInquiry] = useState<boolean>(false);

  async function submitForm() {
    if (successInquiry) return window.alert("already inquiry");

    if (Object.values(form).find((v) => v.conditions.find((vv) => !vv.func(v.value)))) {
      window.alert("check form");
      return;
    }

    const payload = Object.entries(form).reduce(
      (prev: { [k: keyof typeof form]: string | number | null }, curr) => {
        prev[curr[0]] = curr[1].value;
        return prev;
      },
      { contactType, detailEnquiry: detailEnquiry || null }
    );

    console.log("payload", payload);

    try {
      const fetchResult = await fetch("/api/inquiry", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await fetchResult.json();
      if (!result.result) throw new Error("failed inquiry");

      setSuccessInquiry(true);
      window.alert("success inquiry");
    } catch (err) {
      window.alert(err?.toString());
      console.error(err);
    }
  }

  return (
    <Wrap>
      <Area>
        <P $fontColor={theme.colors.primary} $fontWeight={theme.fontWeights.bold} $fontSize="2.75rem">
          {t("contact-us.title-1")}
        </P>
        <P
          style={{ margin: "20px 0 20px 0" }}
          $fontColor={theme.colors.text.default}
          $fontWeight={theme.fontWeights.bold}
          $fontSize="2.75rem"
        >
          {t("contact-us.title-2")}
        </P>
        <P
          $fontColor={theme.colors.text.grayDark}
          $lineHeight="25px"
          data-aos="fade-up"
          data-aos-easing="ease-out"
          data-aos-delay="200"
          data-aos-duration="500"
        >
          {t("contact-us.desc")}
        </P>
      </Area>
      <Area>
        <P
          style={{ margin: "0 0 25px 0" }}
          $fontColor={theme.colors.text.default}
          $fontWeight={theme.fontWeights.bold}
          $fontSize="28px"
        >
          {t("contact-us.form-title")}
        </P>

        <InputWrap>
          <InputLabel>{t("contact-us.contact-type")}</InputLabel>
          <Select disabled={successInquiry} value={contactType} onChange={(e) => setContactType(e.currentTarget.value)}>
            {CONTACT_TYPES.map((v) => (
              <option key={v.label} value={v.value}>
                {t(v.label)}
              </option>
            ))}
          </Select>
        </InputWrap>

        {Object.entries(form).map(([k, v]) => {
          const findedCondition = v.conditions.find((vv) => !vv.func(v.value));
          const isError = v.beginInput && Boolean(findedCondition);

          return (
            <InputWrap key={k}>
              <InputLabel>{v.label}</InputLabel>
              <Input
                disabled={successInquiry}
                type={v.type}
                $isError={isError}
                onChange={(e) =>
                  setForm({ ...form, [k]: { ...form[k], beginInput: true, value: e.currentTarget.value } })
                }
              />
              {(() => {
                if (!isError) return;

                return (
                  <InputMessage>
                    <img src="/icons/error.svg" />
                    {findedCondition?.msg}
                  </InputMessage>
                );
              })()}
            </InputWrap>
          );
        })}

        <InputWrap>
          <InputLabel>{t("contact-us.form-enquiry")}</InputLabel>
          <Textarea
            disabled={successInquiry}
            maxLength={300}
            value={detailEnquiry}
            onChange={(e) => setDetailEnquiry(e.currentTarget.value)}
          />
        </InputWrap>

        <InputWrap>
          <InputLabel>{t("contact-us.form-desc-1")}</InputLabel>
        </InputWrap>

        <InputWrap>
          <Button $disabled={successInquiry} onClick={submitForm}>
            {t("contact-us.form-submit")}
          </Button>
        </InputWrap>

        <InputWrap>
          <Desc>{t("contact-us.form-desc-2")}</Desc>
          <Desc>
            <Trans
              i18nKey="contact-us.form-recaptcha"
              components={{
                1: (
                  <Link href="https://policies.google.com/privacy?hl=en" target="_blank" style={{ color: "#0D68C5" }} />
                ),
                2: <Link href="https://policies.google.com/terms?hl=en" target="_blank" style={{ color: "#0D68C5" }} />,
              }}
            />
          </Desc>
        </InputWrap>
      </Area>
    </Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  padding: 40px;
  width: calc(100% - 40px);
  height: 1300px;
  background-color: ${theme.colors.background.white};
  background-image: url("/footer-logo.webp");
  background-repeat: no-repeat;
  background-size: contain;
  background-position-y: bottom;
  border-radius: 10px;
  ${media.tablet`
    flex-direction: column;
    height: initial;
  `}
`;

const Area = styled.div`
  width: 865px;
  height: 852px;
  ${media.tablet`
    margin: 0 0 25px 0;
    width: initial;
    height: initial;
  `}
`;

const P = styled.p<IFontProps>`
  color: ${(props) => props.$fontColor};
  font-size: ${(props) => props.$fontSize};
  font-weight: ${(props) => props.$fontWeight};
  white-space: pre-wrap;
  line-height: ${(props) => (props.$lineHeight ? props.$lineHeight : "120%")};
  ${media.desktopMid`
      &#desc {
      font-size: 2.5rem;
    }
  `}
  ${media.tablet`
    margin: 0;
    padding: 0 80px 0 0;
    width: initial;
  `}
`;
const InputWrap = styled.div`
  margin-bottom: 20px;
  padding: 0 160px 0 0;
  ${media.tablet`
    padding: initial;
  `}
`;
const InputLabel = styled.p`
  margin-bottom: 6px;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
`;
const InputMessage = styled.p`
  line-height: 20px;
  margin-top: 6px;
  -webkit-box-align: center;
  align-items: center;
  color: rgb(249, 72, 57);
  display: flex;

  & > img {
    margin-right: 4px;
  }
`;
const Input = styled.input<{ $isError?: boolean }>`
  box-sizing: border-box;
  font-family: inherit;
  font-size: inherit;
  font-style: normal;
  font-weight: normal;
  line-height: inherit;
  width: 100%;
  max-width: 100%;
  border: 1px solid #d1d1d1;
  color: rgb(31, 31, 31);
  background-color: ${theme.colors.background.white};
  box-shadow: rgba(0, 0, 0, 0.04) 1px 1px 3px;
  transition: border-radius 200ms;
  height: 44px;
  padding: 10px 12px;
  border-radius: 4px;

  ${({ $isError }) => Boolean($isError) && `border-color: #f94839;`}

  &:focus {
    stroke: none;
    outline: none;
    border: 1px solid #0d68c5;
  }

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.08) 2px 2px 5px;
  }
`;

const Textarea = styled.textarea`
  box-sizing: border-box;
  font-family: inherit;
  font-size: inherit;
  font-style: normal;
  font-weight: normal;
  line-height: inherit;
  width: 100%;
  max-width: 100%;
  border: 1px solid rgb(209, 209, 209);
  color: rgb(31, 31, 31);
  background-color: ${theme.colors.background.white};
  box-shadow: rgba(0, 0, 0, 0.04) 1px 1px 3px;
  transition: border-radius 200ms;
  height: 44px;
  padding: 10px 12px;
  border-radius: 4px;
  resize: vertical;
  min-height: 95px;

  &:focus {
    stroke: none;
    outline: none;
    border: 1px solid #0d68c5;
  }

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.08) 2px 2px 5px;
  }
`;

const Button = styled.button<{ $disabled: boolean }>`
  box-shadow: rgba(0, 0, 0, 0.04) 1px 1px 3px;
  transition: border-radius 200ms;
  height: unset;
  padding: 10px 12px;
  margin-top: 32px;
  min-height: 56px;
  border-radius: 4px;
  line-height: 20px;
  font-weight: ${theme.fontWeights.bold};
  font-size: 20px;
  background-color: rgb(168, 5, 5);
  border: 0px;
  color: ${theme.colors.text.white};
  cursor: pointer;
  display: flex;
  -webkit-box-align: center;
  align-items: center;
  font-family: inherit;
  font-style: normal;
  -webkit-box-pack: center;
  justify-content: center;
  width: 100%;
  outline: none;

  ${({ $disabled }) =>
    $disabled &&
    `
    filter: brightness(0.7);
  `}
`;

const Desc = styled(InputLabel)`
  margin: 20px 0;
  line-height: 20px;
  font-weight: 400;
  font-size: 14px;
  color: #65686f;
`;

const Select = styled.select`
  margin: 0;
  padding: 0.5rem 1rem 0.35rem;
  width: 100%;
  height: 40px;
  background-color: rgba(0, 0, 0, 0.035);
  box-shadow: none;
  -webkit-box-shadow: none;
  border: none;
  position: relative;
  font-size: 14px;
  border: 1px solid black;
  -webkit-transition: border-color 0.2s ease;
  transition: border-color 0.2s ease;
  border-radius: 10px;
  color: #555;
  appearance: none;
`;
